import { ReactNode } from 'react';

import { fontStyles } from '@/styles/typography';

import { classNames } from '@/utils/classnames';

import { Badge } from '../Badge';
import { ErrorIcon } from '../Icons/ErrorIcon';
import { XIcon } from '../Icons/XIcon';

export type AnnouncementBannerProps = {
  variant?: 'default' | 'error' | 'new';
  title?: string;
  children?: ReactNode;
  onDismiss?: () => void;
};

export const AnnouncementBanner = ({
  variant = 'default',
  title,
  children,
  onDismiss,
}: AnnouncementBannerProps) => {
  const isDismissible = onDismiss != null;

  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      className={classNames(
        'flex items-center bg-special-background-modal w-full',
        "shadow-10 [body[data-show-banner='false']_&]:hidden",
        { 'pl-7 pr-1 py-1': isDismissible },
        { 'py-[9px] px-3': !isDismissible },
      )}
    >
      <div
        className={classNames(
          'flex flex-row items-center min-w-0 grow-1 justify-center',
          variant === 'error' ? 'gap-1' : 'gap-2',
        )}
      >
        {variant === 'error' && <ErrorIcon className="text-token-red-900 size-4 shrink-0" />}
        {variant === 'new' && (
          <Badge variant="positive" className="shadow-green">
            New
          </Badge>
        )}
        {title && (
          <p className={fontStyles({ variant: 'LABEL_MD', class: 'whitespace-nowrap' })}>{title}</p>
        )}
        <div className={fontStyles({ variant: 'BODY_MD', class: 'truncate' })}>{children}</div>
      </div>
      {isDismissible && (
        <button
          className="flex items-center justify-center text-token-gray-800 cursor-pointer size-8 hover:text-token-gray-700"
          aria-label="Dismiss announcement"
          onClick={onDismiss}
          type="button"
        >
          <XIcon className="size-4" />
        </button>
      )}
    </div>
  );
};
